// Chart.js auto-skips/rotates axis tick labels to fit its container; Nivo renders one
// tick per category unconditionally. With many categories (e.g. 100 meetings) that
// smears into unreadable overlapping text, so thin them out ourselves.
export function sparseTickValues(values: readonly string[], maxTicks = 15): string[] {
    if (values.length <= maxTicks) return [...values];
    const step = Math.ceil(values.length / maxTicks);
    return values.filter((_, i) => i % step === 0);
}

// A body with dozens of seats forces dozens of whole-number ticks (one per integer) into
// a ~200px-tall chart, so the tick text overlaps into a solid unreadable block that bleeds
// past the left edge - not just a margin-sizing problem. Step by a "nice" round number
// instead (still landing only on integers, since fractional mandate counts read as nonsense).
export function niceIntegerTicks(maxValue: number, maxTicks = 6): number[] {
    if (maxValue <= 0) return [0];
    if (maxValue <= maxTicks) return Array.from({ length: maxValue + 1 }, (_, i) => i);

    const roughStep = maxValue / maxTicks;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const residual = roughStep / magnitude;
    const niceResidual = residual > 5 ? 10 : residual > 2 ? 5 : residual > 1 ? 2 : 1;
    const step = Math.max(1, Math.round(niceResidual * magnitude));

    const ticks: number[] = [];
    for (let value = 0; value <= maxValue; value += step) ticks.push(value);
    return ticks;
}

// Nivo positions axisLeft tick labels end-anchored just inside the chart's left margin
// and never grows that margin to fit them, so a margin sized for e.g. 1-2 digit tick
// values clips as soon as a body has enough seats (national parliaments) to need 3
// digits. Estimate the widest label's pixel width so the margin always has room.
export function estimateAxisLeftMargin(labels: readonly string[], minMargin = 60): number {
    const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
    if (longest === 0) return minMargin;
    const estimatedTextWidth = longest * 8; // conservative per-character width for the tick font
    const tickClearance = 10; // default Nivo tickSize + tickPadding
    const legendClearance = 20; // space reserved for the rotated axis title next to the ticks
    return Math.max(minMargin, Math.ceil(estimatedTextWidth + tickClearance + legendClearance));
}
